import React, { useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';

/* ── tokens ── */
const T = {
  navy:       '#1a1a2e',
  indigo:     '#3730A3',
  indigoLight:'#f5f3ff',
  accent:     '#a78bfa',
  muted:      '#4a4a6a',
  border:     '#e8e6f0',
  bg:         '#ffffff',
  serif:      "'Times New Roman', serif",
};

const GlobalStyle = createGlobalStyle`
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
`;

const PageContainer = styled.div`
  margin-top: 195px;
  background: ${T.bg};
  font-family: ${T.serif};
  color: ${T.navy};
  min-height: 100vh;

  @media (max-width: 1366px) and (min-width: 769px) { margin-top: 158px; }
  @media (max-width: 768px) { margin-top: 120px; }
`;

const Hero = styled.div`
  background: ${T.navy};
  display: flex;
  align-items: center;
  min-height: 220px;
  padding: 3rem 5%;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    background: linear-gradient(180deg, ${T.accent} 0%, ${T.indigo} 100%);
  }

  @media (max-width: 768px) {
    min-height: 180px;
    padding: 2.5rem 5%;
    &::before { width: 100%; height: 4px; top: 0; left: 0; right: 0; bottom: auto; }
  }
`;

const HeroWatermark = styled.div`
  position: absolute;
  right: 3%;
  top: 50%;
  transform: translateY(-50%);
  font-family: ${T.serif};
  font-size: 150px;
  font-weight: 900;
  color: rgba(255,255,255,0.025);
  pointer-events: none;
  user-select: none;
  letter-spacing: -8px;
  line-height: 1;
  z-index: 1;

  @media (max-width: 768px) { display: none; }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
`;

const HeroTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(167, 139, 250, 0.1);
  border: 1px solid rgba(167, 139, 250, 0.25);
  color: ${T.accent};
  font-family: ${T.serif};
  font-size: 0.78rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 0.4rem 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  font-weight: 700;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: ${T.accent};
    border-radius: 50%;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.7rem;
    margin-bottom: 1rem;
  }
`;

const HeroTitle = styled.h1`
  font-family: ${T.serif};
  font-size: 3rem;
  font-weight: 900;
  color: #fff;
  line-height: 1.1;
  letter-spacing: -0.02em;

  @media (max-width: 1366px) and (min-width: 769px) { font-size: 2.3rem; }
  @media (max-width: 768px) { font-size: 1.8rem; }
`;

const HeroSubtitle = styled.p`
  font-family: ${T.serif};
  font-size: 1.1rem;
  color: rgba(255,255,255,0.6);
  font-style: italic;
  margin-top: 0.5rem;

  @media (max-width: 1366px) and (min-width: 769px) { font-size: 0.95rem; }
`;

const Wrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem 5rem;

  @media (max-width: 768px) { padding: 2rem 1.2rem 4rem; }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 1000px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const GalleryCard = styled.div`
  background: #fff;
  border: 1px solid ${T.border};
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 12px rgba(55, 48, 163, 0.03);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(55, 48, 163, 0.08);
    border-color: rgba(55, 48, 163, 0.2);
  }
`;

const CardImage = styled.div`
  width: 100%;
  height: 240px;
  overflow: hidden;
  background: ${T.indigoLight};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  ${GalleryCard}:hover img {
    transform: scale(1.05);
  }

  @media (max-width: 1366px) and (min-width: 769px) { height: 200px; }
`;

const CardBody = styled.div`
  padding: 1.2rem;
`;

const CardTitle = styled.h3`
  font-family: ${T.serif};
  font-size: 1.1rem;
  font-weight: 800;
  color: ${T.navy};
  margin-bottom: 0.3rem;
`;

const CardDate = styled.p`
  font-family: ${T.serif};
  font-size: 0.82rem;
  color: ${T.muted};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${T.muted};
  font-family: ${T.serif};
  font-size: 1.1rem;
  font-style: italic;
`;

const events = [
  {
    title: "AHIMA Global Conference 2024",
    date: "October 12, 2024",
    image: "/images/gallery/external/event1.jpg"
  },
  {
    title: "AAPC Healthcon 2024",
    date: "April 5, 2024",
    image: "/images/gallery/external/event2.jpg"
  },
  {
    title: "Medical Coding Industry Meetup",
    date: "July 20, 2024",
    image: "/images/gallery/external/event3.jpg"
  },
  {
    title: "Healthcare RCM Summit",
    date: "February 8, 2024",
    image: "/images/gallery/external/event4.jpg"
  },
  {
    title: "CDI Leadership Forum",
    date: "August 15, 2024",
    image: "/images/gallery/external/event5.jpg"
  },
  {
    title: "Coding & Compliance Conference",
    date: "May 28, 2024",
    image: "/images/gallery/external/event6.jpg"
  },
];

const ExternalEvents = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <GlobalStyle />
      <PageContainer>
        <Hero>
          <HeroWatermark>EE</HeroWatermark>
          <HeroContent>
            <HeroTag>Gallery</HeroTag>
            <HeroTitle>External Events</HeroTitle>
            <HeroSubtitle>Industry conferences, meetups, and external engagements</HeroSubtitle>
          </HeroContent>
        </Hero>

        <Wrap>
          {events.length > 0 ? (
            <GalleryGrid>
              {events.map((event, i) => (
                <GalleryCard key={i}>
                  <CardImage>
                    <img src={event.image} alt={event.title} />
                  </CardImage>
                  <CardBody>
                    <CardTitle>{event.title}</CardTitle>
                    <CardDate>{event.date}</CardDate>
                  </CardBody>
                </GalleryCard>
              ))}
            </GalleryGrid>
          ) : (
            <EmptyState>No external events to display yet.</EmptyState>
          )}
        </Wrap>
      </PageContainer>
    </>
  );
};

export default ExternalEvents;